/**
 * Reads a prototype snapshot zip into path/content entries — the shape both restore paths need:
 * the WebContainer one writes them through `wc.fs`, the light preview's writes them into the
 * workbench store (light-preview plan F2). Shared so the two can never disagree about which
 * files a snapshot holds, or about which of them are binary.
 */

import { bytesToBase64 } from '../runtime/tool-adapters/writeAsset';
import { normalizeWorkbenchPath } from './normalizeWorkbenchPath';
import { parseSnapshotZipBlob } from './parseSnapshotZipBlob';
import type { SnapshotPlanEntry } from './snapshotSwitchPlan';

/** Extensions we treat as binary; .svg stays UTF-8 text. */
export const BINARY_EXT =
  /\.(png|jpe?g|gif|webp|ico|woff2?|ttf|eot|pdf|mp4|webm|mp3|wav|zip|wasm|avif)$/i;

/** `u8` is carried for the WebContainer path, which writes the raw bytes. */
export type SnapshotZipEntry = SnapshotPlanEntry & { u8?: Uint8Array };

export function normalizeZipPath(path: string): string | null {
  let p = path.replace(/^\.\/+/, '').replace(/\\/g, '/');
  if (!p || p.endsWith('/')) return null;
  if (p.startsWith('__MACOSX/') || p.includes('/__MACOSX/')) return null;
  if (p === '.DS_Store' || p.endsWith('/.DS_Store')) return null;
  return normalizeWorkbenchPath(p);
}

export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException('Snapshot restore aborted', 'AbortError');
  }
}

export async function readSnapshotZipEntries(
  blob: Blob,
  signal?: AbortSignal
): Promise<SnapshotZipEntry[]> {
  const zipEntries = await parseSnapshotZipBlob(blob);
  const decoder = new TextDecoder('utf-8', { fatal: false });
  const entries: SnapshotZipEntry[] = [];
  for (const { path, u8 } of zipEntries) {
    throwIfAborted(signal);
    const normalized = normalizeZipPath(path);
    if (!normalized) continue;
    entries.push(
      BINARY_EXT.test(normalized)
        ? { path: normalized, isBinary: true, u8, base64: bytesToBase64(u8) }
        : { path: normalized, isBinary: false, text: decoder.decode(u8) }
    );
  }
  return entries;
}
